import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Lock,
  Unlock,
  Gift,
  Sparkles,
  Trophy,
  Calendar,
  Eye,
  EyeOff,
  Info,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp,
  Search,
  Play,
  RefreshCw,
  HelpCircle,
  ExternalLink,
  Clock
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import BankVault from './BankVault';

// Character reveal sound - dramatic "unlock" effect
const playCharacterRevealSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (freq, startTime, duration, volume = 0.3) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + startTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration);
      
      oscillator.start(audioContext.currentTime + startTime);
      oscillator.stop(audioContext.currentTime + startTime + duration);
    };
    
    // Dramatic ascending sequence
    playTone(196, 0, 0.15, 0.2);
    playTone(220, 0.12, 0.15, 0.25);
    playTone(262, 0.24, 0.15, 0.3);
    playTone(330, 0.4, 0.2, 0.35);
    playTone(392, 0.55, 0.2, 0.4);
    playTone(523, 0.7, 0.4, 0.45);
    playTone(784, 0.95, 0.15, 0.2);
    playTone(1047, 1.05, 0.3, 0.15);
    
  } catch (e) {
    // Silent fail
  }
};

const MONTHS = [
  { num: 1, name: 'Janvier', short: 'Jan' },
  { num: 2, name: 'Février', short: 'Fév' },
  { num: 3, name: 'Mars', short: 'Mar' },
  { num: 4, name: 'Avril', short: 'Avr' },
  { num: 5, name: 'Mai', short: 'Mai' },
  { num: 6, name: 'Juin', short: 'Juin' },
  { num: 7, name: 'Juillet', short: 'Juil' },
  { num: 8, name: 'Août', short: 'Août' },
  { num: 9, name: 'Septembre', short: 'Sep' },
  { num: 10, name: 'Octobre', short: 'Oct' },
  { num: 11, name: 'Novembre', short: 'Nov' },
  { num: 12, name: 'Décembre', short: 'Déc' }
];

// Bank Vault Unlock Animation
const VaultUnlockAnimation = ({ isUnlocking, onComplete, prize }) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (isUnlocking) {
      const stages = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000];
      stages.forEach((delay, i) => {
        setTimeout(() => setStage(i + 1), delay);
      });
      setTimeout(() => onComplete?.(), 5000);
    }
  }, [isUnlocking, onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md">
      <div className="relative text-center">
        {/* Sparkles */}
        {stage >= 6 && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(30)].map((_, i) => {
              // Pre-calculate positions for each sparkle
              const positions = [
                { left: 15, top: 20 }, { left: 85, top: 15 }, { left: 45, top: 80 },
                { left: 25, top: 60 }, { left: 75, top: 40 }, { left: 55, top: 10 },
                { left: 10, top: 45 }, { left: 90, top: 70 }, { left: 35, top: 30 },
                { left: 65, top: 85 }, { left: 5, top: 75 }, { left: 95, top: 25 },
                { left: 40, top: 50 }, { left: 60, top: 65 }, { left: 20, top: 90 },
                { left: 80, top: 55 }, { left: 30, top: 5 }, { left: 70, top: 95 },
                { left: 50, top: 35 }, { left: 12, top: 88 }, { left: 88, top: 12 },
                { left: 22, top: 33 }, { left: 78, top: 77 }, { left: 42, top: 22 },
                { left: 58, top: 92 }, { left: 8, top: 58 }, { left: 92, top: 42 },
                { left: 33, top: 67 }, { left: 67, top: 8 }, { left: 48, top: 48 }
              ];
              const pos = positions[i] || { left: 50, top: 50 };
              const delays = [0, 0.1, 0.2, 0.3, 0.4, 0.05, 0.15, 0.25, 0.35, 0.45, 0.08, 0.18, 0.28, 0.38, 0.48, 0.02, 0.12, 0.22, 0.32, 0.42, 0.07, 0.17, 0.27, 0.37, 0.47, 0.03, 0.13, 0.23, 0.33, 0.43];
              const durations = [0.5, 0.6, 0.7, 0.8, 0.9, 0.55, 0.65, 0.75, 0.85, 0.95, 0.52, 0.62, 0.72, 0.82, 0.92, 0.58, 0.68, 0.78, 0.88, 0.98, 0.53, 0.63, 0.73, 0.83, 0.93, 0.57, 0.67, 0.77, 0.87, 0.97];
              return (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-amber-400 rounded-full animate-ping"
                  style={{
                    left: `${pos.left}%`,
                    top: `${pos.top}%`,
                    animationDelay: `${delays[i]}s`,
                    animationDuration: `${durations[i]}s`,
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Animated Vault */}
        <div className={`relative w-48 h-48 md:w-64 md:h-64 mx-auto transition-transform duration-1000 ${stage >= 2 ? 'scale-110' : ''} ${stage >= 4 ? 'animate-bounce' : ''}`}>
          {/* Vault body */}
          <div className={`absolute inset-0 rounded-full transition-all duration-500 ${
            stage >= 7 ? 'bg-gradient-to-br from-green-400 to-emerald-600' : 'bg-gradient-to-br from-slate-500 via-slate-400 to-slate-600'
          } border-8 ${stage >= 5 ? 'border-amber-400' : 'border-slate-300'} shadow-2xl`}>
            
            {/* Inner ring */}
            <div className={`absolute inset-4 rounded-full transition-all duration-500 ${
              stage >= 7 ? 'bg-gradient-to-br from-emerald-600 to-green-800' : 'bg-gradient-to-br from-slate-600 to-slate-800'
            } border-4 border-slate-500`}>
              
              {/* Center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`w-20 h-20 md:w-28 md:h-28 rounded-full flex items-center justify-center transition-all duration-500 ${
                  stage >= 7 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-xl shadow-amber-500/50' : 'bg-gradient-to-br from-slate-700 to-slate-900'
                }`}>
                  {stage >= 7 ? (
                    <Unlock className="w-10 h-10 md:w-14 md:h-14 text-white animate-pulse" />
                  ) : stage >= 3 ? (
                    <div className="text-4xl md:text-5xl animate-spin" style={{ animationDuration: '0.5s' }}>🔐</div>
                  ) : (
                    <Lock className="w-10 h-10 md:w-14 md:h-14 text-amber-400" />
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Glow effect */}
          {stage >= 5 && (
            <div className="absolute inset-0 rounded-full bg-amber-400/30 blur-xl animate-pulse" />
          )}
        </div>

        {/* Text stages */}
        <div className="mt-8 space-y-2">
          {stage >= 1 && <p className="text-amber-400 text-lg animate-pulse">Vérification de la combinaison...</p>}
          {stage >= 3 && <p className="text-cyan-400 text-lg animate-pulse">Déverrouillage en cours...</p>}
          {stage >= 5 && <p className="text-green-400 text-xl font-bold animate-bounce">Combinaison correcte !</p>}
          {stage >= 7 && (
            <div className="mt-6 p-6 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-2xl border border-amber-500/50">
              <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-4 animate-bounce" />
              <h3 className="text-3xl font-bold text-white mb-2">🎉 FÉLICITATIONS !</h3>
              <p className="text-amber-300 text-xl mb-4">Vous avez déverrouillé le coffre !</p>
              <div className="bg-slate-800/50 rounded-xl p-4">
                <Gift className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-white font-bold text-lg">{prize}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Secret Character Input Component
const SecretCharacterInput = ({ month, hint, onSubmit, isSubmitting, hasSubmitted, submittedChar }) => {
  const [character, setCharacter] = useState('');
  const [showHint, setShowHint] = useState(false);

  const handleSubmit = () => {
    if (character.length === 1) {
      onSubmit(month, character.toUpperCase());
      setCharacter('');
    }
  };

  if (hasSubmitted) {
    return (
      <div className="flex items-center gap-2 p-2 bg-green-500/20 rounded-lg border border-green-500/30">
        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
        <span className="text-green-300 text-sm">Code trouvé : <strong className="text-lg">{submittedChar}</strong></span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Hint display */}
      <div className="flex items-start gap-2">
        <button 
          onClick={() => setShowHint(!showHint)}
          className="flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs"
        >
          <HelpCircle className="w-3 h-3" />
          {showHint ? 'Masquer' : 'Voir'} l&apos;indice
        </button>
      </div>
      
      {showHint && hint && (
        <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/20 text-xs text-purple-300">
          💡 {hint}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2">
        <Input
          value={character}
          onChange={(e) => setCharacter(e.target.value.slice(0, 1).toUpperCase())}
          placeholder="?"
          maxLength={1}
          className="w-12 h-10 text-center text-lg font-mono bg-slate-800 border-purple-500/30 text-white uppercase"
        />
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!character || isSubmitting}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          {isSubmitting ? '...' : 'Valider'}
        </Button>
      </div>
    </div>
  );
};

// Main Annual Chest Game Component
const AnnualChestGame = ({ year, isAuthenticated }) => {
  const [progress, setProgress] = useState(null);
  const [annualGame, setAnnualGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSecrets, setShowSecrets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showUnlockAnimation, setShowUnlockAnimation] = useState(false);
  const [monthlyQuizzes, setMonthlyQuizzes] = useState([]);
  
  // Test Mode disabled in production

  const fetchData = async () => {
    try {
      // Fetch annual game info (public)
      const gameRes = await api.get(`/game/annual-game/${year}`);
      setAnnualGame(gameRes.data);

      // Fetch monthly quizzes for this year
      try {
        const quizzesRes = await api.get(`/game/contests?year=${year}`);
        const filtered = (quizzesRes.data || []).filter(q => q.month && q.year === year);
        setMonthlyQuizzes(filtered);
      } catch (e) {
        console.log('No quizzes data');
      }

      // Fetch player progress if authenticated (check token directly)
      const token = localStorage.getItem('game_token');
      console.log('Token found:', !!token);
      if (token) {
        try {
          const progressRes = await api.get(`/game/annual-game/${year}/my-progress?token=${token}`);
          console.log('Progress data:', progressRes.data);
          setProgress(progressRes.data);
          
          // Test mode disabled in production
        } catch (err) {
          console.log('No progress yet:', err);
        }
      }
    } catch (e) {
      console.error('Error fetching annual game:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [year, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when token appears (after login)
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('game_token');
      if (token && !progress) {
        fetchData();
      }
    };
    
    // Check immediately
    checkToken();
    
    // Listen for storage changes (cross-tab)
    window.addEventListener('storage', checkToken);
    
    // Poll for token every 2 seconds (same-tab login detection)
    const interval = setInterval(checkToken, 2000);
    
    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, [progress]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitSecret = async (month, character) => {
    const token = localStorage.getItem('game_token');
    if (!token) return;

    setSubmitting(true);
    try {
      const res = await api.post(`/game/annual-game/${year}/submit-secret?token=${token}`, {
        month,
        character
      });

      if (res.data.success) {
        if (res.data.already_found) {
          toast.info(res.data.message);
        } else {
          // Play character reveal sound for new character discovery
          playCharacterRevealSound();
          toast.success(res.data.message);
        }
        fetchData();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryUnlock = async () => {
    const token = localStorage.getItem('game_token');
    if (!token) return;

    try {
      const res = await api.post(`/game/annual-game/${year}/try-unlock?token=${token}`);
      if (res.data.success) {
        if (res.data.is_winner) {
          setShowUnlockAnimation(true);
          toast.success("🏆 VOUS ÊTES LE GRAND GAGNANT !");
        }
      } else {
        if (res.data.already_won) {
          toast.info(`Le coffre a déjà été ouvert par ${res.data.winner_pseudo} !`);
        } else if (res.data.not_yet) {
          toast.warning(res.data.message);
        } else {
          toast.error(res.data.message);
        }
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleUnlockComplete = () => {
    setTimeout(() => {
      setShowUnlockAnimation(false);
      fetchData();
    }, 2000);
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-800/50 rounded-2xl h-64" />
    );
  }

  if (!annualGame) {
    return null;
  }

  // Use test progress if in test mode, otherwise use real progress
  // Progress data - test mode disabled
  const activeProgress = progress;
  const displayCombo = progress?.display_combination || Array(24).fill('_');
  const charactersEarned = progress?.characters_earned || 0;
  const completedMonths = progress?.completed_months || [];
  const secretMonths = progress?.secret_months || [];
  const secretHints = annualGame?.secret_hints || [];
  const isComplete = charactersEarned === 24;
  const chestUnlocked = progress?.chest_unlocked;
  const gameName = annualGame?.name || 'Le Coffre MSC';
  const hasWinner = annualGame?.winner_id;
  const winnerPseudo = annualGame?.winner_pseudo;

  // Calculate unlocked months (both quiz completed AND secret found)
  const unlockedMonthsCount = MONTHS.filter(m => 
    completedMonths.includes(m.num) && secretMonths.includes(m.num)
  ).length;

  return (
    <>
      {/* Unlock Animation Overlay */}
      {showUnlockAnimation && (
        <VaultUnlockAnimation 
          isUnlocking={true} 
          onComplete={handleUnlockComplete}
          prize={annualGame?.prize}
        />
      )}

      <Card className="bg-gradient-to-br from-amber-900/30 via-slate-800/50 to-amber-900/30 border-amber-500/30 overflow-hidden relative">
        {/* Decorative corners */}
        <div className="absolute top-0 left-0 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-500/20 to-transparent" />
        <div className="absolute bottom-0 right-0 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-tl from-amber-500/20 to-transparent" />
        
        {/* Winner Banner */}
        {hasWinner && (
          <div className="bg-gradient-to-r from-yellow-500 to-amber-500 px-3 sm:px-4 py-2 sm:py-3">
            <div className="flex items-center justify-center gap-2 text-white">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="font-bold text-sm sm:text-lg">🏆 GAGNANT : {winnerPseudo}</span>
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <p className="text-center text-white/80 text-xs sm:text-sm">
              Coffre ouvert le {annualGame?.won_at ? new Date(annualGame.won_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '24 décembre'} !
            </p>
          </div>
        )}
        
        <CardHeader className="relative pb-2 sm:pb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg ${
                hasWinner 
                  ? 'bg-gradient-to-br from-yellow-400 to-amber-500 shadow-yellow-500/30' 
                  : 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/30'
              }`}>
                {chestUnlocked || hasWinner ? (
                  <Unlock className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                ) : (
                  <Lock className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                )}
              </div>
              <div>
                <CardTitle className="text-white flex flex-wrap items-center gap-2 text-base sm:text-lg md:text-xl">
                  {gameName} {year}
                  {chestUnlocked && <Badge className="bg-green-500 text-xs">DÉVERROUILLÉ !</Badge>}
                  {hasWinner && !chestUnlocked && <Badge className="bg-yellow-500 text-xs">REMPORTÉ</Badge>}
                </CardTitle>
                <p className="text-amber-400 text-xs sm:text-sm">
                  {hasWinner 
                    ? `🏆 Remporté par ${winnerPseudo}` 
                    : chestUnlocked 
                      ? '🎉 Félicitations !' 
                      : `${charactersEarned}/24 caractères collectés`
                  }
                </p>
              </div>
            </div>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs sm:text-sm">
              <Gift className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">{annualGame?.prize}</span>
              <span className="sm:hidden">Gros lot</span>
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 sm:space-y-6">
          {/* Bank Vault Visual - Responsive */}
          <div className="flex justify-center py-2 sm:py-4">
            <BankVault
              totalMonths={12}
              unlockedMonths={unlockedMonthsCount}
              totalCharacters={24}
              earnedCharacters={charactersEarned}
              isFullyUnlocked={isComplete || chestUnlocked}
              size={window.innerWidth < 640 ? 'small' : 'medium'}
              showLabels={true}
              animate={true}
            />
          </div>

          {/* Prize Highlight - Responsive */}
          <div className="bg-amber-500/10 rounded-xl p-3 sm:p-4 border border-amber-500/30">
            <div className="flex items-center justify-center gap-2 text-amber-400 mb-1">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Gros lot du 24 décembre</span>
            </div>
            <p className="text-center text-base sm:text-lg md:text-xl font-bold text-white">{annualGame?.prize}</p>
            {annualGame?.prize_description && (
              <p className="text-center text-xs sm:text-sm text-slate-400 mt-1">{annualGame?.prize_description}</p>
            )}
          </div>

          {/* Combination Display - Responsive */}
          <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4">
            <h3 className="text-white font-medium mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <Sparkles className="w-4 h-4 text-amber-400" />
              Votre Combinaison
            </h3>
            
            {/* Character grid */}
            <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
              {displayCombo.map((char, i) => {
                const monthIndex = Math.floor(i / 2);
                const isQuizChar = i % 2 === 0;
                const hasChar = char !== '_' && char !== '';
                
                return (
                  <div key={i} className="relative group">
                    <span 
                      className={`w-6 h-7 sm:w-8 sm:h-9 md:w-9 md:h-10 flex items-center justify-center rounded text-sm sm:text-base md:text-lg font-mono transition-all ${
                        hasChar 
                          ? isQuizChar
                            ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/30' 
                            : 'bg-white text-red-600 border-2 border-red-300 shadow-lg shadow-red-200/30 font-bold'
                          : 'bg-white/10 text-white/50 border border-white/20'
                      }`}
                    >
                      {hasChar ? char : '?'}
                    </span>
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 rounded text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {MONTHS[monthIndex]?.short} - {isQuizChar ? 'Quiz' : 'Indice'}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex justify-center gap-4 sm:gap-6 mt-3 text-[10px] sm:text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-red-600 border border-red-400" /> Quiz
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-white border border-red-300 text-red-600 text-[8px] flex items-center justify-center font-bold">A</span> Indice
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-white/10 border border-white/30" /> À découvrir
              </span>
            </div>
          </div>

          {/* Monthly Progress - Responsive */}
          {(isAuthenticated || progress) && !hasWinner && (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-white font-medium flex items-center gap-2 text-sm sm:text-base">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  Progression Mensuelle
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSecrets(!showSecrets)}
                    className="text-purple-400 hover:text-purple-300 text-xs sm:text-sm"
                  >
                    <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    {showSecrets ? 'Masquer' : 'Codes mystères'}
                    {showSecrets ? <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4 ml-1" /> : <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />}
                  </Button>
                </div>
              </div>

              {/* Monthly Grid - Responsive */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                {MONTHS.map((month) => {
                  const quizCompleted = completedMonths.includes(month.num);
                  const secretFound = secretMonths.includes(month.num);
                  const hint = secretHints.find(h => h.month === month.num);
                  const quiz = monthlyQuizzes.find(q => q.month === month.num);
                  const currentMonth = new Date().getMonth() + 1;
                  const isPastMonth = month.num < currentMonth;
                  const isCurrentMonth = month.num === currentMonth;

                  return (
                    <div 
                      key={month.num}
                      className={`p-2 sm:p-3 rounded-lg border transition-all ${
                        quizCompleted && secretFound
                          ? 'bg-green-500/20 border-green-500/30'
                          : isCurrentMonth
                            ? 'bg-amber-500/20 border-amber-500/30'
                            : isPastMonth
                                ? 'bg-slate-700/50 border-slate-600/30'
                                : 'bg-slate-800/50 border-slate-700/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium text-xs sm:text-sm">{month.short}</span>
                        {quizCompleted && secretFound ? (
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                        ) : isCurrentMonth ? (
                          <Badge className="bg-amber-500 text-[8px] sm:text-[10px] px-1">En cours</Badge>
                        ) : null}
                      </div>

                      {/* Quiz Status */}
                      <div className="flex items-center gap-1 mb-1 text-[10px] sm:text-xs">
                        {quizCompleted ? (
                          <>
                            <Trophy className="w-3 h-3 text-amber-400" />
                            <span className="text-cyan-300">Quiz ✓</span>
                          </>
                        ) : quiz && (isPastMonth || isCurrentMonth) ? (
                          <Link to={`/jeu/${quiz.id}/jouer`}>
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white text-[10px] h-5 px-2"
                            >
                              <Play className="w-2 h-2 mr-1" />
                              Jouer
                            </Button>
                          </Link>
                        ) : isPastMonth ? (
                          <>
                            <RefreshCw className="w-3 h-3 text-orange-400" />
                            <span className="text-orange-300">Rattrapage</span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-3 h-3 text-slate-500" />
                            <span className="text-slate-500">À partir du 1er {month.name.toLowerCase()}</span>
                          </>
                        )}
                      </div>

                      {/* Secret Status */}
                      <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                        {secretFound ? (
                          <>
                            <CheckCircle className="w-3 h-3 text-purple-400" />
                            <span className="text-purple-300">Secret ✓</span>
                          </>
                        ) : hint ? (
                          <Button
                            size="sm"
                            onClick={() => setShowSecrets(true)}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-[10px] h-5 px-2"
                          >
                            <Eye className="w-2 h-2 mr-1" />
                            Indice
                          </Button>
                        ) : (
                          <>
                            <Search className="w-3 h-3 text-purple-400" />
                            <span className="text-slate-400">Secret</span>
                          </>
                        )}
                      </div>

                      {/* Secret Input (when expanded) - works for both normal and test mode */}
                      {showSecrets && !secretFound && hint && (
                        <div className="mt-2 pt-2 border-t border-slate-700">
                          <SecretCharacterInput
                            month={month.num}
                            hint={hint.hint}
                            onSubmit={handleSubmitSecret}
                            isSubmitting={submitting}
                            hasSubmitted={secretFound}
                            submittedChar={displayCombo[month.num * 2 - 1]}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Try unlock button */}
          {(isAuthenticated || progress) && isComplete && !chestUnlocked && !hasWinner && (
            <div className="text-center space-y-3">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 sm:p-4 inline-block">
                <p className="text-amber-300 text-xs sm:text-sm mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Ouverture possible uniquement le <strong>24 décembre {year}</strong>
                </p>
                <p className="text-amber-200/70 text-[10px] sm:text-xs">Soyez le premier à ouvrir le coffre pour remporter le lot !</p>
              </div>
              <div>
                <Button 
                  size="lg"
                  onClick={handleTryUnlock}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-sm sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-xl shadow-lg shadow-amber-500/30 animate-pulse"
                >
                  <Lock className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  TENTER D&apos;OUVRIR LE COFFRE !
                </Button>
              </div>
            </div>
          )}

          {/* Game already won by someone else */}
          {hasWinner && !chestUnlocked && (
            <div className="text-center p-4 sm:p-6 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-xl border border-yellow-500/30">
              <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">🏆 COFFRE REMPORTÉ !</h3>
              <p className="text-yellow-300 text-sm sm:text-lg mb-2">Félicitations à <strong>{winnerPseudo}</strong> !</p>
              <p className="text-slate-400 text-xs sm:text-sm">
                Le coffre a été ouvert en premier par ce joueur chanceux.
              </p>
            </div>
          )}

          {/* Chest unlocked message (current player is the winner) */}
          {chestUnlocked && (
            <div className="text-center p-4 sm:p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
              <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-amber-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">🎉 COFFRE DÉVERROUILLÉ !</h3>
              <p className="text-green-300 text-sm sm:text-lg mb-3 sm:mb-4">Vous avez gagné :</p>
              <div className="inline-block bg-amber-500/20 px-4 sm:px-6 py-2 sm:py-3 rounded-xl border border-amber-500/30">
                <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-amber-300 font-bold text-base sm:text-xl">{annualGame?.prize}</p>
              </div>
              <p className="text-slate-400 text-xs sm:text-sm mt-4">
                Notre équipe vous contactera pour la remise de votre lot.
              </p>
            </div>
          )}

          {/* Instructions for non-authenticated */}
          {!isAuthenticated && !progress && (
            <div className="text-center p-3 sm:p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <p className="text-slate-300 text-xs sm:text-sm">
                Connectez-vous pour suivre votre progression et saisir vos codes mystères !
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default AnnualChestGame;
